"use client";

import { useMemo, useState } from "react";
import { CircleHelp } from "lucide-react";
import { FavoriteTeamQuickChip } from "@/components/site/favorite-team-quick-chip";
import { TransfersArticleCard } from "@/components/site/transfers-article-card";

type TransferArticle = {
  id: string;
  title: string;
  excerpt?: string | null;
  coverImage?: string | null;
  slug: string;
  category?: { name: string } | null;
  publishedAt?: Date | string | null;
  gridSize: string;
  openable?: boolean | null;
  tags: { tag: { name: string; slug: string } }[];
};

type TransferRailItem =
  | { type: "normal"; articles: TransferArticle[] }
  | { type: "featured"; article: TransferArticle };

interface TransfersFilteredRailProps {
  articles: TransferArticle[];
  favoriteTeam?: { name: string; logoUrl: string | null; externalTeam?: { crest: string | null } | null } | null;
  initialTagQuery?: string;
}

const TRANSFER_ARTICLE_LABEL = "LASZTI365 · ÁTIGAZOLÁSI PLETYKÁK";

function buildTransferRailItems(articles: TransferArticle[]) {
  const items: TransferRailItem[] = [];
  let normalColumn: TransferArticle[] = [];

  articles.forEach((article) => {
    if (article.gridSize === "featured") {
      if (normalColumn.length > 0) {
        items.push({ type: "normal", articles: normalColumn });
        normalColumn = [];
      }

      items.push({ type: "featured", article });
      return;
    }

    normalColumn.push(article);

    if (normalColumn.length === 2) {
      items.push({ type: "normal", articles: normalColumn });
      normalColumn = [];
    }
  });

  if (normalColumn.length > 0) {
    items.push({ type: "normal", articles: normalColumn });
  }

  return items;
}

function matchesTagQuery(article: TransferArticle, query: string) {
  if (!query) return true;

  return article.tags.some(({ tag }) => {
    const name = tag.name.toLocaleLowerCase("hu-HU");
    const slug = tag.slug.toLocaleLowerCase("hu-HU");

    return name.includes(query) || slug.includes(query);
  });
}

export function TransfersFilteredRail({ articles, favoriteTeam, initialTagQuery = "" }: TransfersFilteredRailProps) {
  const [tagQuery, setTagQuery] = useState(initialTagQuery);
  const normalizedQuery = tagQuery.trim().toLocaleLowerCase("hu-HU");

  const filteredArticles = useMemo(
    () => articles.filter((article) => matchesTagQuery(article, normalizedQuery)),
    [articles, normalizedQuery],
  );
  const railItems = useMemo(() => buildTransferRailItems(filteredArticles), [filteredArticles]);
  const hasMatches = filteredArticles.length > 0;

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-6 md:gap-8">
      <div className="w-full md:max-w-xl">
        <label className="flex items-center gap-3 border-b border-black/20 pb-2 text-[#111111] transition-colors focus-within:border-[#A4243B]">
          <CircleHelp
            aria-hidden="true"
            className="size-4 shrink-0 text-[#A4243B]"
            strokeWidth={1.8}
          />
          <input
            type="search"
            value={tagQuery}
            onChange={(event) => setTagQuery(event.target.value)}
            placeholder="Keresés címkékre..."
            title="Írj be egy címkét, például: Premier League, Juventus, Mbappé"
            className="min-w-0 flex-1 bg-transparent font-display text-base font-semibold text-[#111111] outline-none placeholder:text-[#8A8175]"
          />
        </label>
        <div className="mt-3">
          <FavoriteTeamQuickChip
            team={
              favoriteTeam
                ? {
                    name: favoriteTeam.name,
                    logoUrl: favoriteTeam.logoUrl,
                    externalTeam: favoriteTeam.externalTeam,
                    href: `/transfers?team=${encodeURIComponent(favoriteTeam.name)}`,
                  }
                : null
            }
            onUse={() => setTagQuery(favoriteTeam?.name ?? "")}
          />
        </div>
      </div>

      {!hasMatches ? (
        <div className="flex min-h-[280px] items-center py-12 text-sm font-medium text-[#6B6256] md:min-h-[584px]">
          <p>Nincs találat erre a címkére.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-6 md:hidden">
            {filteredArticles.map((article, index) => (
              <TransfersArticleCard
                key={article.id}
                article={article}
                index={index}
                labelOverride={TRANSFER_ARTICLE_LABEL}
              />
            ))}
          </div>

          <div className="hidden min-w-0 w-full flex-1 snap-x snap-mandatory gap-8 overflow-x-auto pr-8 md:flex">
            {railItems.map((item, itemIndex) => {
              if (item.type === "featured") {
                return (
                  <div
                    key={item.article.id}
                    className="h-[584px] w-[430px] shrink-0 snap-start"
                  >
                    <TransfersArticleCard
                      article={item.article}
                      index={itemIndex}
                      className="h-full"
                      labelOverride={TRANSFER_ARTICLE_LABEL}
                    />
                  </div>
                );
              }

              return (
                <div
                  key={item.articles.map((article) => article.id).join("-")}
                  className="grid w-[370px] shrink-0 snap-start grid-rows-[repeat(2,280px)] gap-6"
                >
                  {item.articles.map((article, articleIndex) => (
                    <TransfersArticleCard
                      key={article.id}
                      article={article}
                      index={itemIndex + articleIndex}
                      className="h-full"
                      labelOverride={TRANSFER_ARTICLE_LABEL}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
